import type { Action, ConfigBundle, ContentVariant, IngestBatch, Rule, TriggerNode } from "@tracking-web/shared";
import { createContext, normalizePagePath, type Context } from "./context.js";
import { evaluate, triggerUsesBackIntent, triggerFiredByTransient, triggerRefsSignal } from "./engine.js";
import { createAdapters, maskSelectorsOfAction, type EmitFn } from "./adapters.js";
import { fetchConfig, findScript, resolveOrigin } from "./config.js";
import { readCachedCountry, fetchGeo } from "./geo.js";
import { createTransport } from "./transport.js";
import { uid } from "./util.js";
import { startReading } from "./collectors/reading.js";
import { startExitIntent } from "./collectors/exitIntent.js";
import { startBackGuard } from "./collectors/backGuard.js";
import { startIdle } from "./collectors/idle.js";
import { startSectionTime } from "./collectors/sectionTime.js";
import { startClicks } from "./collectors/clicks.js";
import { startHover } from "./collectors/hover.js";
import { startQuiz } from "./collectors/quiz.js";
import { startAds, bindAdsEmit, makeAdImpressionGate } from "./collectors/ads.js";
import { startConsent } from "./collectors/consent.js";
import { startExperiments } from "./experiments.js";
import { startBandits } from "./bandit.js";
import { installRouteWatcher } from "./router.js";

type State = {
  ctx: Context;
  emit: EmitFn;
  flush: () => void;
  subscribers: Record<string, ((v: unknown) => void)[]>;
};

let started = false;

// --- Estado compartido entre bundles del MISMO realm ---
// El SDK es un IIFE: si el snippet se incluye DOS veces (dos plugins de WordPress,
// GTM + snippet directo…), se evalúan dos bundles, cada uno con su propio scope de
// módulo. Sin coordinación → doble boot: page_view duplicado y listeners de tracking
// duplicados (DOBLE CONTEO del dato — inaceptable). Por eso el arranque se "reclama"
// en un flag global (claimGlobalBoot) y tanto el estado vivo como el diagnóstico SPA
// viven en un registro global compartido: así solo arranca un bundle y window.TW de
// CUALQUIER bundle opera sobre la única instancia viva (no queda una muerta pisando).
type TwGlobals = {
  booted?: boolean;
  state?: State | null;
  spa?: { routeChanges: number; lastHref: string };
};
const sharedFallback: TwGlobals = {}; // entorno sin window (SSR/worker): estado local
function tw(): TwGlobals {
  try {
    // Nombre específico (no "__tw") para minimizar colisión con globals de terceros.
    const w = window as unknown as { __twRuntime?: TwGlobals };
    return (w.__twRuntime ||= {});
  } catch {
    return sharedFallback;
  }
}

// Diagnóstico SPA (contador de cambios de ruta detectados en cliente). Vive en el
// registro compartido para que, bajo doble carga, refleje la instancia viva y no
// quede huérfano. Solo introspección; sin efectos sobre la página.
const spaDebug = (tw().spa ||= { routeChanges: 0, lastHref: "" });

/** Reclama el arranque para este realm. Devuelve false si ya lo reclamó otro bundle
 *  (o este). El claim es SÍNCRONO → cierra la ventana de DOBLE BOOT. Ojo: el estado
 *  vivo (tw().state) no existe hasta el final de boot(); si boot falla, el claim se
 *  LIBERA (ver boot) para que un segundo include o reintento pueda recuperar. */
function claimGlobalBoot(): boolean {
  const g = tw();
  if (g.booted) return false;
  g.booted = true;
  return true;
}

// El origen del servicio se resuelve del <script> SÍNCRONAMENTE (currentScript
// solo está disponible durante la ejecución inicial del bundle).
const SERVICE_ORIGIN = resolveOrigin();

function domReady(): Promise<void> {
  if (document.readyState !== "loading") return Promise.resolve();
  return new Promise((resolve) =>
    document.addEventListener("DOMContentLoaded", () => resolve(), { once: true })
  );
}

function getAnonId(): { anonId: string; visitCount: number } {
  let anonId = "";
  let visitCount = 1;
  try {
    anonId = localStorage.getItem("tw_anon") || "";
    if (!anonId) {
      anonId = uid();
      localStorage.setItem("tw_anon", anonId);
    }
    visitCount = Number(localStorage.getItem("tw_visits") || "0");
  } catch {
    anonId = anonId || uid();
  }
  return { anonId, visitCount };
}

function getSession(): { sessionId: string; isNew: boolean } {
  try {
    const existing = sessionStorage.getItem("tw_session");
    if (existing) return { sessionId: existing, isNew: false };
    const sessionId = uid();
    sessionStorage.setItem("tw_session", sessionId);
    return { sessionId, isNew: true };
  } catch {
    return { sessionId: uid(), isNew: true };
  }
}

async function boot(siteKey: string) {
  if (started) return;
  started = true;

  const config: ConfigBundle | null = await fetchConfig(SERVICE_ORIGIN, siteKey);
  if (!config) {
    // Un blip transitorio de /api/config (red/CORS/5xx) no debe dejar el SDK muerto
    // toda la sesión: liberamos el claim para que un segundo include del snippet o un
    // init() posterior puedan reintentar el arranque.
    started = false;
    tw().booted = false;
    return;
  }

  // KILL TOTAL remoto: enabled:false → el SDK no hace NADA más (ni collectors, ni reglas, ni
  // experimentos, ni eventos), como si el snippet no estuviera. Dejamos el claim tomado: es un
  // apagado INTENCIONAL, no un fallo transitorio que reintentar. Propaga en ~60s (caché de
  // config), sin redeplegar sdk.js. (Toma efecto en la PRÓXIMA carga; una sesión ya arrancada
  // sigue hasta que el usuario recarga/navega — igual que si el cliente quitara el snippet.)
  if (config.enabled === false) return;

  await domReady(); // los collectors necesitan el DOM parseado

  const ctx = createContext();
  const { anonId, visitCount } = getAnonId();
  const { sessionId, isNew } = getSession();
  ctx.state.visitCount = visitCount + (isNew ? 1 : 0);

  // País del visitante (condiciones de reglas/tests). De la caché de localStorage es SÍNCRONO:
  // si hay veredicto, `country` ya está puesto para la evaluación inicial → una regla de país
  // aplica en el primer render, sin RTT. `null` = sin veredicto (visitante nuevo o caché caducada):
  // se resolverá por red más abajo, con anti-flicker si hay reglas de país. Ver geo.ts.
  const cachedCountry = readCachedCountry();
  ctx.state.country = cachedCountry ?? "";
  const geoUndecided = cachedCountry === null;
  // Mientras el país NO tiene veredicto (visitante nuevo, esperando /api/geo), NO se evalúa ningún
  // trigger que referencie `country`: con country="" un trigger NEGATIVO (`nin`/`not in`) casaría y
  // aplicaría/EXPONDRÍA prematuramente (la exposición A/B es un latch irreversible → contaminaría el
  // test). `geoPending` se pone a true solo si de verdad hay que esperar (ver bloque geo) y a false al
  // resolver/timeout; entonces se re-evalúa con el veredicto real. deferCountry lo consulta el motor de
  // reglas, y las capas A/B y bandit vía su opción `deferTrigger`.
  let geoPending = false;
  const deferCountry = (t: TriggerNode): boolean => geoPending && triggerRefsSignal(t, "country");
  if (isNew) {
    try {
      localStorage.setItem("tw_visits", String(ctx.state.visitCount));
    } catch {
      /* noop */
    }
  }

  // Meta de sesión por referencia: startConsent muta tz/cons aquí y, como el
  // transporte reserializa el envelope en cada flush, los cambios entran solos.
  const meta: NonNullable<IngestBatch["m"]> = {
    url: location.href,
    ref: document.referrer || undefined,
    ua: navigator.userAgent,
    dev: ctx.state.device,
  };
  const transport = createTransport({
    origin: SERVICE_ORIGIN,
    siteKey,
    sessionId,
    anonId,
    sampleRate: config.sampleRate ?? 1,
    meta,
  });

  // Referencia mutable: emit necesita avisar a la capa A/B, que se crea después.
  let experiments: {
    tick: (s: typeof ctx.state) => void;
    handleEvent: EmitFn;
    armableOnBack: (s: typeof ctx.state) => boolean;
  } | null = null;
  // Capa bandit: decide/pinta por cambio de señal (tick), se reinicia por navegación SPA y
  // se actualiza (update) al re-pedir config si cambió configVersion.
  let bandits: ReturnType<typeof startBandits> | null = null;

  const subscribers: State["subscribers"] = {};
  const emit: EmitFn = (type, props, url) => {
    transport.track(type, props, url);
    experiments?.handleEvent(type, props);
    const subs = subscribers[type];
    if (subs) for (const cb of subs) cb(props);
  };

  // Variantes en un Map para que los adapters las resuelvan por key.
  const variants = new Map<string, ContentVariant>();
  for (const v of config.variants) variants.set(v.key, v);
  const adapters = createAdapters({ variants, emit });

  // Motor de reglas: re-evalúa en cada cambio de señal (debounced a un frame).
  // `rules` es reasignable: la Fase 3 lo reconstruye al re-pedir config en navegación.
  let rules: Rule[] = [...config.rules]
    .filter((r) => r.enabled)
    .sort((a, b) => b.priority - a.priority);
  const fires = new Map<string, number>();

  // Cooldown persistente por NAVEGADOR: timestamp (ms) de la última vez que se mostró una
  // regla con cooldown, guardado en localStorage. A diferencia de `fires` (en memoria, se
  // reinicia por página/navegación SPA), esto sobrevive recargas (MPA), navegación SPA y
  // sesiones → "no volver a mostrar este popup en las próximas N horas". Si localStorage no
  // está disponible (modo privado), degrada a "sin cooldown": nunca rompe, solo no limita.
  function cooldownKey(ruleId: string): string {
    return "tw_cd_" + ruleId;
  }
  function inCooldown(rule: Rule): boolean {
    if (!rule.cooldownHours) return false;
    try {
      const last = Number(localStorage.getItem(cooldownKey(rule.id)));
      if (!Number.isFinite(last) || last <= 0) return false;
      const elapsed = Date.now() - last;
      // Timestamp en el FUTURO (reloj del visitante atrasado tras haberlo sellado): no atrapes el
      // popup "para siempre" → trátalo como caducado y deja que se muestre (re-sella un `last` sano).
      if (elapsed < 0) return false;
      return elapsed < rule.cooldownHours * 3600000;
    } catch {
      return false;
    }
  }
  function markShown(rule: Rule): void {
    if (!rule.cooldownHours) return;
    try {
      localStorage.setItem(cooldownKey(rule.id), String(Date.now()));
    } catch {
      /* sin localStorage → no persistimos el cooldown (degradación segura) */
    }
  }

  // Snapshot de experimentos del boot (no se recargan en caliente — Fase 4). Capturado en el flujo
  // lineal, donde `config` ya está garantizado no-nulo, para que las closuras de abajo no tropiecen
  // con su tipo nullable. Los bandits SÍ se recargan (maybeRefreshConfig), así que su lista es
  // mutable y se reasigna en el refresh (no un snapshot congelado).
  const bootExperiments = config.experiments ?? [];
  let banditConfigs = config.bandits ?? [];

  // Caché ESTÁTICA: ¿la config referencia la señal de "Atrás" en ALGÚN sitio (regla/exp/bandit)?
  // Recalcularla es O(reglas) pero solo ocurre al cambiar la config (boot / refresh), NO en cada
  // gesto. backIntentArmable() se consulta en CADA gesto del usuario (camino caliente); sin esta
  // caché, los ~todos los publishers SIN regla de Atrás pagarían un recorrido del árbol de
  // condiciones de TODAS sus reglas en cada pointerdown/touchstart/keydown.
  let backIntentInConfig = false;
  function recomputeBackIntentInConfig() {
    backIntentInConfig =
      rules.some((r) => triggerUsesBackIntent(r.trigger)) ||
      bootExperiments.some((e) => !!e.trigger && triggerUsesBackIntent(e.trigger)) ||
      banditConfigs.some((b) => !!b.trigger && triggerUsesBackIntent(b.trigger));
  }
  recomputeBackIntentInConfig();

  // ¿Hay AHORA MISMO algo que se mostraría al pulsar Atrás? El back-guard lo consulta antes de
  // empujar el centinela: así solo tocamos el historial cuando el popup/variante de verdad saldría.
  // Camino caliente: 1 booleano para el 99% que no usa la feature; el recorrido caro solo si la
  // config la usa. Para no SOBRE-armar (empujar un centinela que un Atrás no llegaría a mostrar):
  //  - REGLAS: evaluamos el trigger COMPLETO con `backExitIntent` puesta a true ("¿saldría si pulsa
  //    Atrás AHORA?") + el gating de regla (maxFires/cooldown) → respeta las demás condiciones AND.
  //  - EXPERIMENTOS/BANDITS: DELEGAMOS en su capa (armableOnBack), que conoce status, URL del bandit,
  //    asignación (control/noop) y exposición previa → no armamos para algo que no pintará (evita
  //    "tragar" el Atrás del usuario sin mostrar nada, p. ej. un bandit de otra URL o un experimento
  //    ya expuesto).
  // `rules` se reasigna en el re-fetch de config (SPA) → esto lo ve en vivo; las capas A/B leen sus
  // propias listas vivas.
  function backIntentArmable(): boolean {
    if (!backIntentInConfig) return false; // atajo barato para quien no usa la feature
    const asIfBack = { ...ctx.state, backExitIntent: true }; // "como si pulsara Atrás ya"
    for (const rule of rules) {
      if (!triggerUsesBackIntent(rule.trigger)) continue;
      if ((fires.get(rule.id) || 0) >= rule.maxFiresPerSession) continue;
      if (inCooldown(rule)) continue;
      if (!evaluate(rule.trigger, asIfBack)) continue; // otras condiciones del trigger sin cumplir → no armar
      return true;
    }
    return !!experiments?.armableOnBack(ctx.state) || !!bandits?.armableOnBack(ctx.state);
  }

  function runRules() {
    for (const rule of rules) {
      const count = fires.get(rule.id) || 0;
      if (count >= rule.maxFiresPerSession) continue;
      if (inCooldown(rule)) continue; // dentro de la ventana de cooldown → no re-mostrar (popups)
      if (deferCountry(rule.trigger)) continue; // país sin veredicto aún → no decidir esta regla todavía
      if (!evaluate(rule.trigger, ctx.state)) continue;
      // Aplica las acciones. En SPA el page view virtual corre en el pushState, ANTES de
      // que el framework monte el DOM de la página nueva: si NINGUNA acción se aplicó
      // (anchor/selector aún ausente), NO consumimos el disparo → se reintenta en el
      // próximo tick (idle 1s / scroll / siguiente navegación), cuando el DOM ya esté.
      // Así una regla no "se gasta" sin llegar a pintar la variante.
      let applied = false;
      // Si la regla se dispara por una señal TRANSITORIA (intención de salir: Atrás o exit-intent de
      // ratón), su popup es EFÍMERO → se desmonta al navegar, no se queda pegado sobre la página a la
      // que se vuelve. "Necesaria" (no solo referenciada): un trigger compuesto disparado por el OTRO
      // brazo no marca el popup efímero.
      const ephemeral = triggerFiredByTransient(rule.trigger, ctx.state);
      for (const action of rule.actions) {
        if (adapters.apply(action, rule.id, ephemeral)) applied = true;
      }
      if (!applied) continue;
      fires.set(rule.id, count + 1);
      markShown(rule); // sella el timestamp de cooldown SOLO cuando el popup se mostró de verdad
      emit("rule_matched", { ruleId: rule.id, name: rule.name });
    }
  }
  function evaluateRules() {
    runRules();
    experiments?.tick(ctx.state); // experimentos con trigger
    bandits?.tick(ctx.state); // bandits: reintenta pintado / aplica si la URL ya casa
  }
  ctx.onChange(evaluateRules);

  // ¿Vamos a ESPERAR el país? (sin veredicto en caché Y la config usa la señal `country`). Hay que
  // decidirlo AQUÍ, ANTES de construir las capas A/B/bandit: startBandits aplica sus bandits en la
  // CONSTRUCCIÓN (barrido inicial), así que si activáramos geoPending después, un bandit de país se
  // serviría/logearía (bandit_view + propensity, IRREVERSIBLE) con country="". El bloque de máscara +
  // fetch de más abajo se engancha a este mismo flag. Bandits: solo `running` (los demás no se aplican
  // → pedir /api/geo por ellos sería malgastar la petición y la espera).
  if (geoUndecided) {
    geoPending =
      rules.some((r) => triggerRefsSignal(r.trigger, "country")) ||
      bootExperiments.some(
        (e) =>
          e.status !== "stopped" &&
          e.status !== "paused" &&
          e.goalType !== "rps" && // rps pinta por UTM (no por trigger de cliente) → no esperamos país
          !!e.trigger &&
          triggerRefsSignal(e.trigger, "country")
      ) ||
      banditConfigs.some(
        (b) => b.status === "running" && !!b.trigger && triggerRefsSignal(b.trigger, "country")
      );
  }

  // Capa A/B: asignación 50/50 + exposición + atribución de objetivo.
  experiments = startExperiments({
    experiments: config.experiments ?? [],
    anonId,
    ctx,
    adapters,
    emit,
    deferTrigger: deferCountry,
  });

  // Capa bandit contextual: decisión Thompson sticky por URL + bandit_view + propensity.
  bandits = startBandits({
    bandits: config.bandits ?? [],
    anonId,
    ctx,
    adapters,
    emit,
    deferTrigger: deferCountry,
  });

  // Selectores a ocultar por el anti-flicker de país: acciones que MUTAN contenido on-load de reglas/
  // tests/bandits con condición de país (modal/popup = overlay, no parpadea; rps = pinta por el marcador
  // UTM del redirect, NO por trigger de cliente → enmascararlo sería puro flicker). Se recomputa por
  // página porque `rules`/`banditConfigs` cambian en la navegación SPA. try/catch: config rara nunca
  // tumba el boot (sin máscara, los triggers de país no casan sin veredicto → no pintan igualmente).
  function countryMaskSelectors(): string[] {
    const selSet = new Set<string>();
    try {
      const gated: Action[] = [];
      for (const r of rules) if (triggerRefsSignal(r.trigger, "country")) gated.push(...r.actions);
      for (const e of bootExperiments) {
        if (e.status === "stopped" || e.status === "paused" || e.goalType === "rps") continue;
        if (e.trigger && triggerRefsSignal(e.trigger, "country"))
          for (const v of e.variants) gated.push(v.action as Action);
      }
      for (const b of banditConfigs) {
        if (b.status === "running" && b.trigger && triggerRefsSignal(b.trigger, "country"))
          for (const arm of b.arms) gated.push(arm.action as Action);
      }
      for (const a of gated) for (const s of maskSelectorsOfAction(a)) selSet.add(s);
    } catch {
      /* config rara → sin máscara */
    }
    return Array.from(selSet);
  }

  // --- Resolución de PAÍS + anti-flicker (solo si el país aún no tiene veredicto) ---
  // El país sale de la IP → solo el servidor lo ve; lo pedimos a /api/geo (una vez por visitante,
  // cacheado en localStorage). SOLO si la config usa la señal `country` en algún trigger: un publisher
  // sin reglas de país no hace NINGUNA petición geo. Mientras resolvemos, ocultamos lo que las reglas
  // de país afectan on-load (no mostrar el contenido equivocado); al llegar el país re-evaluamos y
  // revelamos, y si no llega a tiempo (timeout) revelamos y decide el resto de condiciones (fallback).
  const GEO_WAIT_MS = 1200; // techo de espera del país antes de revelar y aplicar las demás condiciones
  if (geoPending) {
    adapters.maskTargets(countryMaskSelectors()); // no-op si no hay selectores (p. ej. solo popups de país)

    let revealed = false;
    const reveal = () => {
      if (revealed) return;
      revealed = true;
      adapters.revealTargets();
    };
    // Aplica el veredicto del país (el real, o "" del timeout): deja de aplazar, re-evalúa y revela
    // SIEMPRE (finally) — aunque evaluateRules lance (selector inválido en un apply), el contenido nunca
    // queda oculto para siempre. Puede correr DOS veces (el timeout con "" y luego el país real que llega
    // tarde): la 2ª con el país REAL es DELIBERADA — un visitante que resuelve tras el timeout debe recibir
    // su variante (evaluateRules es idempotente: latches de exposición + maxFires). Con "" ninguna
    // condición de país casa (ver engine) → no aplica ni expone nada de país (fallback = "no hacer nada").
    const applyGeoVerdict = (country: string) => {
      geoPending = false;
      ctx.state.country = country;
      try {
        evaluateRules();
      } finally {
        reveal();
      }
    };
    // Fallback: si el país no llega en GEO_WAIT_MS (geo lento/caído), resolvemos como desconocido ("").
    const geoTimer = setTimeout(() => applyGeoVerdict(""), GEO_WAIT_MS);

    void fetchGeo(SERVICE_ORIGIN).then((country) => {
      clearTimeout(geoTimer);
      applyGeoVerdict(country); // si llega TRAS el timeout, re-evalúa con el país REAL (no se pierde)
    });
  }

  // Collectors.
  const reading = startReading(ctx, emit);
  startExitIntent(ctx, emit);
  // Back-guard: centinela en el historial para detectar el botón Atrás (intención de volver)
  // y mostrar un popup/variante antes de que el usuario se vaya. Solo arma si hay regla, test
  // A/B o bandit de Atrás armable (backIntentArmable) → cero impacto en el historial para quien
  // no usa la feature.
  const backGuard = startBackGuard(ctx, emit, backIntentArmable);
  // Desmonte de popups EFÍMEROS (intención de salir) en una NAVEGACIÓN del usuario hacia atrás/adelante
  // (popstate), aunque sea a un estado del MISMO pathname (query/hash) — caso que onVirtualPageview
  // no cubre por su early-return. Atado a `popstate` (acción del usuario), NO a cualquier cambio de
  // href: así un `replaceState`/`pushState` del propio sitio (limpieza de ?utm, scroll-spy, sync de
  // filtros) NO destruye el popup que el usuario tiene abierto. En el pop del centinela aún no hay
  // popup (se pinta en el rAF posterior), así que esto no mata el que está a punto de aparecer.
  try {
    window.addEventListener(
      "popstate",
      () => {
        try {
          adapters.teardownOverlays(true);
        } catch {
          /* nunca rompe la página del publisher */
        }
      },
      { passive: true }
    );
  } catch {
    /* entorno sin addEventListener: degradación silenciosa */
  }
  const idle = startIdle(ctx, emit, config.idleThresholdMs ?? 8000);
  // Tiempo viewable por franja (mapa de atención). Se engancha al tick de idle (un solo
  // reloj) → reparte SOLO el crédito de tiempo activo entre las franjas visibles.
  // Kill-switch remoto (como spaEnabled): sectionTimeEnabled:false lo apaga sin redeploy.
  const sectionTime =
    config.sectionTimeEnabled !== false ? startSectionTime(emit, idle.onTick) : null;
  startClicks(ctx, emit);
  startHover(ctx);
  startQuiz(ctx, emit);
  startConsent(meta); // zona horaria (síncrono) + suscripción al CMP; muta `meta`
  // El listener de impresiones de ads (startAds) ya se enganchó al ejecutarse el SDK,
  // ANTES de este boot async (ver init/autoInit), para no perder la 1ª impresión en
  // cargas rápidas. Aquí conectamos el emit real + el gate: las tempranas bufferizadas se
  // vuelcan ahora, y a partir de aquí cada impresión solo cuenta con la pestaña visible y el
  // usuario presente (no contar el auto-refresh de un tab abandonado → IPVPS inflado).
  bindAdsEmit(emit, makeAdImpressionGate(ctx));

  // --- Navegación SPA (Fase 2): cada cambio de ruta de cliente = page view virtual ---
  // En publishers SPA (Next/React) la página cambia SIN recargar, así que el SDK nunca
  // volvía a evaluar las reglas con la URL nueva (el bug original). Aquí tratamos cada
  // cambio de ruta como una página nueva PARA LAS REGLAS: reseteamos las señales
  // por-página, limpiamos `fires` (maxFiresPerSession cuenta por página virtual, igual
  // que en MPA donde cada carga reinicia), re-detectamos el artículo, emitimos un
  // page_view virtual y re-evaluamos las reglas.
  //
  // SOLO reglas: la semántica SPA de experimentos (re-exposición, atribución de tiempo)
  // es Fase 4. Por eso runRules() (no evaluateRules()) y NO tocamos activeReadingMs ni
  // el ciclo de exposición → el tiempo de sesión y los objetivos de experimentos quedan
  // exactamente como hoy.
  function onVirtualPageview(href: string) {
    spaDebug.routeChanges += 1; // diagnóstico: cuenta TODO cambio de href (incl. query/hash)
    spaDebug.lastHref = href;
    const next = normalizePagePath(location.pathname);
    if (next === ctx.state.pageUrl) return; // solo query/hash → no es page view
    // Fase 4: vuelca el tiempo activo de la página SALIENTE (A) ANTES de cambiar de página.
    // En SPA un pushState no emite page_exit/active_time, así que sin esto el objetivo
    // time_on_page del experimento quedaría en el último heartbeat (o 0 si la estancia fue
    // <5s) cuando el sello lo congela. Volcar aquí captura el tiempo real hasta la salida.
    sendActive(false);
    lastSentActive = 0; // T2: la página nueva reinicia activeReadingMs → reinicia el baseline del inc
    // Vuelca los tramos de tiempo-por-sección de la página SALIENTE con SU url (el colector
    // la tiene guardada) ANTES de cambiar de página; luego apunta a la nueva. sendActive(false)
    // ya corrió idle.settle() → los últimos ms de la saliente ya están acreditados a sus franjas.
    sectionTime?.reset();
    // Desmonta los popups de la página SALIENTE: viven en document.body, fuera del árbol del
    // framework, así que un pushState no los retira → sin esto se APILARÍAN al re-disparar la
    // regla en la página nueva. Paridad con MPA (la recarga los mata). Sin emitir dismiss.
    adapters.teardownOverlays();
    ctx.resetForPage(location.pathname);
    fires.clear();
    reading.reset();
    // Anti-flicker de país en SPA: si aún esperamos el veredicto (ventana ~1,2s del 1er render), oculta
    // también los objetivos de país de la página NUEVA — la máscara del boot solo cubría los de la
    // anterior. Al resolver, applyGeoVerdict → revealTargets los revela todos. (country="" hasta el
    // settle → una condición de país no casa, así que no se pinta nada de país todavía.)
    if (geoPending) adapters.maskTargets(countryMaskSelectors());
    // Pathname nuevo = página nueva → permitir re-disparo del popup de Atrás (resetea `fired`). El
    // re-armado por cambio de URL EXACTA (query/hash) lo hace el propio arm() del back-guard, sin
    // depender de aquí (autosuficiente, también con spaEnabled=false). El falso positivo en
    // navegación query/hash lo bloquea la defensa por marcador en onPopstate.
    backGuard.resetForPage();
    emit("page_view", {
      url: location.href,
      title: document.title,
      referrer: ctx.state.referrer,
      device: ctx.state.device,
      visitCount: ctx.state.visitCount,
      virtual: true,
    });
    runRules();
    // Bandit: la URL cambió → olvida lo aplicado y re-decide/inyecta para la página nueva
    // (sticky por anonId, así que la variante del visitante no cambia).
    bandits?.reset();
    bandits?.tick(ctx.state);
    void maybeRefreshConfig(); // Fase 3: una regla nueva aparece sin refresh (throttled)
  }

  // --- Re-fetch de config en navegación (Fase 3) ---
  // En SPA el SDK no recargaba nunca → una regla recién creada en el dashboard no
  // aparecía hasta un refresh duro. Aquí, en cada page view virtual, re-pedimos la
  // config (con throttle, alineado al CDN) y, si cambió la configVersion, reconstruimos
  // reglas y variantes y re-evaluamos. SOLO reglas/variantes: actualizar experimentos en
  // caliente (re-asignación/exposición) es Fase 4 — aquí no se tocan.
  let currentConfigVersion = config.configVersion;
  let lastConfigFetchAt = Date.now(); // la config se acaba de descargar en el boot
  const configRefreshThrottleMs = config.configRefreshThrottleMs ?? 60000;

  async function maybeRefreshConfig() {
    const now = Date.now();
    if (now - lastConfigFetchAt < configRefreshThrottleMs) return;
    lastConfigFetchAt = now; // optimista: bloquea re-fetch concurrente / en ráfaga
    const fresh = await fetchConfig(SERVICE_ORIGIN, siteKey);
    if (!fresh) return; // fallo transitorio → conservamos la config vigente
    if (fresh.configVersion === currentConfigVersion) return; // sin cambios reales
    currentConfigVersion = fresh.configVersion;
    rules = [...fresh.rules].filter((r) => r.enabled).sort((a, b) => b.priority - a.priority);
    banditConfigs = fresh.bandits ?? []; // lo lee recomputeBackIntentInConfig (gate estático); sync con bandits.update
    variants.clear(); // mismo Map (lo capturan los adapters por referencia)
    for (const v of fresh.variants) variants.set(v.key, v);
    recomputeBackIntentInConfig(); // una regla/bandit de Atrás nueva (o retirada) re-arma/des-arma el guard
    runRules(); // aplica reglas nuevas que ya casen en la página actual
    // Bandits: posteriors reentrenados, altas/bajas y pausas frescas (paridad con reglas).
    bandits?.update(fresh.bandits ?? []);
    bandits?.tick(ctx.state);
  }
  // Kill-switch remoto: spaEnabled=false (en el config del proyecto) desactiva la detección
  // de navegación SPA → vuelve al comportamiento pre-SPA (por carga), sin redeplegar sdk.js.
  if (config.spaEnabled !== false) {
    installRouteWatcher(onVirtualPageview, {
      history,
      location,
      addEventListener: window.addEventListener.bind(window),
    });
  }

  // Tiempo activo: heartbeats incrementales + flush final en page_exit. Medir el
  // tiempo SOLO al salir es frágil (el beacon de cierre se pierde a menudo en
  // móvil/bfcache, e incluso visibilitychange tiene bugs de navegador); con
  // heartbeats periódicos capturamos casi todo el tiempo activo aunque el
  // page_exit final no llegue. `inc` = ms activos desde el último envío; el
  // servidor los suma. No se manda si no hay tiempo activo nuevo (pestaña oculta /
  // usuario inactivo).
  //
  // 5s = alineado con el flush del transporte (no tiene sentido latir más rápido:
  // los eventos esperarían igual al siguiente flush). Debe ser MÁS CORTO que la
  // sesión típica o nunca llega a dispararse; con sesiones de ~7-35s, 5s da varios
  // latidos por sesión. Coste de red nulo (el flush ya iba cada 5s) y los
  // active_time no se persisten como filas de evento.
  //
  // Nota: las impresiones de anuncios NO viajan aquí. Cada una se emite como un
  // evento discreto `ad_impression` (ver collectors/ads.ts) → auditable y deduplicado.
  const HEARTBEAT_MS = 5000;
  let lastSentActive = 0;
  function sendActive(final: boolean) {
    idle.settle(); // acredita hasta este instante antes de medir el incremento
    const inc = ctx.state.activeReadingMs - lastSentActive;
    if (inc <= 0 && !final) return;
    lastSentActive = ctx.state.activeReadingMs;
    emit(final ? "page_exit" : "active_time", {
      inc,
      activeReadingMs: ctx.state.activeReadingMs,
      sessionActiveMs: ctx.state.sessionActiveMs, // acumulado de sesión → el servidor toma el MÁX
      timeOnPageMs: ctx.state.timeOnPageMs,
      // Pico REAL de lectura (estado continuo). El evento `reading_progress` se coalesce a deciles,
      // así que el servidor NO puede derivar maxReading de su pct (infravaloraría: un lector que para
      // a mitad de decil, o con pico <10%, perdería su pico). Lo llevamos aquí (NEVER_SAMPLE, ya
      // alimenta `derived`) para fundirlo en maxReading desde el estado, no desde la emisión.
      readingProgressPct: ctx.state.readingProgressPct,
    });
  }
  setInterval(() => sendActive(false), HEARTBEAT_MS);

  // Salida de página: incremento final + acumulados (estos últimos los siguen
  // leyendo dashboard y la atribución de tiempo de los tests A/B). Flush explícito
  // para que el beacon incluya el page_exit. Se dispara al ocultar la pestaña y en
  // pagehide; reenviar no infla (el inc final es ~0 al estar oculta y el servidor
  // suma incrementos, no acumulados).
  function sendExit() {
    sendActive(true); // idle.settle() corre aquí → acredita el último tramo a las franjas
    sectionTime?.flush(); // cierra y emite los tramos abiertos ANTES del beacon de cierre
    transport.flushOnClose(); // write-ahead + beacon: sobrevive al cierre, drena en la próxima carga
  }
  document.addEventListener(
    "visibilitychange",
    () => {
      if (document.hidden) sendExit();
    },
    { passive: true }
  );
  window.addEventListener("pagehide", sendExit, { passive: true });

  // Eventos de arranque.
  if (isNew) emit("session_start", { device: ctx.state.device });
  emit("page_view", {
    url: location.href,
    title: document.title,
    referrer: ctx.state.referrer,
    device: ctx.state.device,
    visitCount: ctx.state.visitCount,
  });

  // Evaluación inicial (por si alguna regla ya se cumple, p. ej. por visitCount).
  evaluateRules();

  tw().state = { ctx, emit, flush: transport.flush, subscribers };
}

// ---- API pública (window.TW) ----
export function init(siteKey: string) {
  if (!claimGlobalBoot()) return; // doble include del snippet → no re-arrancar
  startAds(); // engancha el listener de ads ANTES del boot async (no perder la 1ª impresión)
  void boot(siteKey);
}
export function track(name: string, props?: Record<string, unknown>) {
  tw().state?.emit(name, props);
}
export function identify(traits?: Record<string, unknown>) {
  // Prototipo: sin PII por defecto. Reservado para Advanced Matching futuro.
  if (traits) tw().state?.emit("identify", traits);
}
export function on(signal: string, cb: (v: unknown) => void) {
  const s = tw().state;
  if (!s) return;
  (s.subscribers[signal] ||= []).push(cb);
}
export function flush() {
  tw().state?.flush();
}
export function getContext() {
  return tw().state?.ctx.state ?? null;
}
/** Diagnóstico SPA (solo lectura): nº de cambios de ruta detectados y último href. */
export function __spaDebug() {
  return { ...(tw().spa ?? spaDebug) };
}

// ---- Auto-init desde data-site-key ----
(function autoInit() {
  const siteKey = findScript()?.getAttribute("data-site-key");
  if (siteKey) {
    if (!claimGlobalBoot()) return; // doble include del snippet → no re-arrancar
    startAds(); // engancha el listener de ads ANTES del boot async (no perder la 1ª impresión)
    void boot(siteKey);
  }
})();
